import { globalConfig, globalManager } from '@/utils/globalConfig';
import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';
import { sceneUtils } from '@/utils/sceneUtils';

/**
 * 关键点信息接口
 */
export interface Keyframe {
    /** 关键点唯一标识 */
    id: string;
    /** 关键点在时间轴上的位置（刻度值） */
    position: number;
    /** 关键点索引 */
    index: number;
    /** 额外的自定义数据 */
    data?: any;
}

/**
 * 时间轴配置选项
 */
export interface TimelineControlOptions extends ElementOptions {
    /** 时间轴总刻度数 */
    totalTicks?: number;
    /** 轨道宽度（像素） */
    trackWidth?: number;
    /** 是否禁用 */
    disabled?: boolean;
    /** 标签数量 */
    labelCount?: number;
}

/**
 * TimelineControlUI - 时间轴控制组件
 * 支持添加、删除关键点，点击时间轴跳转，显示标签等
 */
export class TimelineControlUI {
    private ui: BootstrapUI;
    private container: HTMLElement;
    private trackContainer: HTMLElement;
    private isPlaying: boolean = false;
    private animationId: number | null = null;
    private lastFrameTime: number = 0;
    private targetFrameTime: number = 1000 / globalConfig.frameRate; // 目标帧间隔（毫秒）
    private trackElement: HTMLElement;
    private labelsContainer: HTMLElement;
    private keyframes: Keyframe[] = [];
    private options: TimelineControlOptions;
    private onChangeCallbacks: ((keyframe: Keyframe) => void)[] = [];
    private onTickClickCallbacks: ((tick: number) => void)[] = [];
    private currentTick: number = 0; // 从1开始，0表示未设置
    private keyframeCounter: number = 0;

    private currentTickLabel: HTMLElement | null = null;
    private currentTickSegment: HTMLElement | null = null;
    private isDragging: boolean = false;
    private dragMouseMoveHandler: ((e: MouseEvent) => void) | null = null;
    private dragMouseUpHandler: ((e: MouseEvent) => void) | null = null;

    constructor(options: TimelineControlOptions = {}) {
        this.ui = bootstrapUI;
        this.options = {
            totalTicks: 100,
            labelCount: 10,
            disabled: false,
            ...options
        };

        this.container = this.createElement();
        this.trackContainer = this.container.querySelector('.timeline-track-container') as HTMLElement;
        this.trackElement = this.container.querySelector('.timeline-track') as HTMLElement;
        this.labelsContainer = this.container.querySelector('.timeline-labels') as HTMLElement;

        this.setupEventHandlers();
        this.updateLabels();
        this.updateCurrentTick(0);
    }

    /**
     * 创建时间轴组件元素
     */
    private createElement(): HTMLElement {
        const container = this.ui.createElement({
            className: ['timeline-control-container', ...(this.options.className ? (Array.isArray(this.options.className) ? this.options.className : [this.options.className]) : [])],
            id: this.options.id,
            attributes: {
                ...(this.options.attributes || {}),
                'data-total-ticks': this.options.totalTicks?.toString() || '100',
                // 'data-track-width': this.options.trackWidth?.toString() || '800'
            }
        });

        // 创建轨道容器
        const trackContainer = this.ui.createElement({
            className: 'timeline-track-container',
            styles: {
                // width: `${this.options.trackWidth}px`
            }
        });

        // 创建轨道
        const track = this.ui.createElement({
            className: 'timeline-track',
            attributes: {
                role: 'slider',
                'aria-label': 'Timeline'
            }
        });

        // 关键点容器
        const keyframesContainer = this.ui.createElement({
            className: 'timeline-keyframes'
        });
        track.appendChild(keyframesContainer);

        // 当前刻度段标记
        const currentTickMarker = this.ui.createElement({
            className: 'timeline-current-tick',
            attributes: {
                'data-tick': '0',
                style: 'display: none;'
            }
        });
        track.appendChild(currentTickMarker);

        // 分段分隔线容器
        const dividersContainer = this.ui.createElement({
            className: 'timeline-dividers'
        });
        track.appendChild(dividersContainer);

        trackContainer.appendChild(track);

        // 标签容器（用于显示刻度段标签）
        const labelsContainer = this.ui.createElement({
            className: 'timeline-labels'
        });

        container.appendChild(trackContainer);
        container.appendChild(labelsContainer);

        return container;
    }

    /**
     * 设置事件处理器
     */
    private setupEventHandlers(): void {
        // 点击轨道事件
        this.ui.on('click', this.trackElement, (e) => {
            if (this.options.disabled) return;
            this.handleTrackClick(e as MouseEvent);
        });
    }

    /**
     * 处理轨道点击
     * 刻度识别的步长为1，currentTick的值仅根据点击位置相对于轨道的位置计算
     * 与标签显示个数、刻度段无关
     */
    private handleTrackClick(e: MouseEvent): void {
        const rect = this.trackElement.getBoundingClientRect();
        const trackWidth = rect.width;

        // 获取点击位置相对于轨道左边缘的偏移量
        const clickX = e.clientX - rect.left;

        // 确保点击位置在轨道范围内
        const clampedClickX = Math.max(0, Math.min(clickX, trackWidth));

        const totalTicks = this.options.totalTicks || 100;

        // 计算点击位置对应的刻度值（步长为1）
        // 将轨道宽度平均分成 totalTicks 段，每段对应1个刻度
        // 点击位置 / 轨道宽度 * 总刻度数 = 对应的刻度位置（0-based）
        // 然后四舍五入得到最近的整刻度，再加1转换为1-based
        const tickPosition = (clampedClickX / trackWidth) * totalTicks;
        const tick = Math.round(tickPosition); // 四舍五入后从0开始
        console.log('点击轨道事件---', tick);

        // 确保在有效范围内（1 到 totalTicks）
        const finalTick = Math.max(0, Math.min(tick, totalTicks - 1));

        this.setCurrentTick(finalTick);
    }

    /**
     * 更新标签显示
     * 标签总数不大于labelCount，第一个刻度标签始终显示，其他标签之间步长一致
     * 标签位置与分隔线位置一致
     */
    private updateLabels(): void {
        if (!this.labelsContainer) return;

        this.labelsContainer.innerHTML = '';
        const totalTicks = this.options.totalTicks || 100;
        const maxLabelCount = this.options.labelCount || 10;

        // 根据轨道宽度判断是否显示标签
        const shouldShowLabels = (this.options.trackWidth || 800) >= 400;

        if (!shouldShowLabels) {
            this.updateDividers();
            return;
        }

        // 确定要显示的标签刻度值
        // 第一个刻度标签始终显示，其他标签对应的分隔线之间步长一致
        // 按固定步长一直显示，直到标签值 > totalTicks
        const tickValuesToShow: number[] = [];

        // 第一个刻度标签始终显示（对应分隔线1）
        tickValuesToShow.push(0);

        // 计算剩余可用的标签数量（除去第一个），用于计算步长
        const remainingSlots = maxLabelCount - 1;

        if (remainingSlots > 0 && totalTicks > 1) {
            // 计算固定的整数步长，使得分隔线之间步长一致
            // 步长应该使得在不超过maxLabelCount的情况下，尽量均匀分布
            // 先计算理想步长，但实际显示时会一直显示到超出totalTicks为止
            const step = Math.floor((totalTicks - 1) / remainingSlots);

            // 如果步长为0，至少为1
            const actualStep = Math.max(1, step);

            // 从第二个标签开始，按固定整数步长计算位置
            // 标签对应的分隔线位置：1, 1+step, 1+2*step, 1+3*step, ...
            // 一直添加直到标签值 > totalTicks
            let i = 1;
            while (true) {
                // 计算分隔线位置（标签刻度值）：1 + i * step
                const tick = 1 + i * actualStep;
                // const tick = i * actualStep;

                // 如果标签值大于 totalTicks，停止添加
                if (tick > totalTicks) {
                    break;
                }

                // 同时也要限制标签总数不超过 maxLabelCount
                // if (tickValuesToShow.length >= maxLabelCount) {
                //     break;
                // }

                // 避免重复，如果不重复则添加
                if (!tickValuesToShow.includes(tick)) {
                    tickValuesToShow.push(tick);
                }

                i++;
            }
        }

        // 排序以确保顺序
        tickValuesToShow.sort((a, b) => a - b);

        // 创建并显示标签
        tickValuesToShow.forEach(tickValue => {
            // 标签位置与分隔线对齐（分隔线位置 = (tickValue - 1) / totalTicks * 100%）
            const labelPosition = ((tickValue) / totalTicks) * 100;

            const label = this.ui.createElement({
                tag: 'span',
                className: 'timeline-label',
                textContent: tickValue.toString(),
                attributes: {
                    'data-tick': tickValue.toString()
                },
                styles: {
                    left: `${labelPosition}%`,
                    position: 'absolute'
                }
            });

            // 添加点击事件
            label.addEventListener('click', (e) => {
                e.stopPropagation();
                // 点击标签时跳到标签对应的刻度值
                console.log('点击标签事件---', tickValue);
                this.setCurrentTick(tickValue);
            });

            this.labelsContainer.appendChild(label);
        });

        // 渲染分段分隔线
        this.updateDividers();
    }

    /**
     * 更新分段分隔线显示
     * 在轨道每个刻度段的开始位置做标记，刻度段总数等于totalTicks，步长为1
     */
    private updateDividers(): void {
        const dividersContainer = this.trackElement.querySelector('.timeline-dividers');
        if (!dividersContainer) return;

        dividersContainer.innerHTML = '';

        const totalTicks = this.options.totalTicks || 100;

        // 在每个刻度段的开始位置创建分隔线
        // 刻度从1开始，共有totalTicks个刻度段
        // 第i个刻度段的开始位置是 (i-1) / totalTicks * 100%
        for (let i = 0; i < totalTicks; i++) {
            // 计算刻度段开始位置（i从1开始，所以位置是(i-1)/totalTicks）
            const dividerPosition = i / totalTicks;
            const dividerPercentage = dividerPosition * 100;

            const divider = this.ui.createElement({
                className: 'timeline-divider',
                attributes: {
                    'data-tick': i.toString()
                },
                styles: {
                    left: `${dividerPercentage}%`,
                    position: 'absolute',
                    top: '0',
                    bottom: '0',
                    width: '1px',
                    backgroundColor: 'red',
                    pointerEvents: 'none'
                }
            });

            dividersContainer.appendChild(divider);
        }
    }
    /**
     * 更新分段分隔线显示
     */
    private updateDividers_old(): void {
        const dividersContainer = this.trackElement.querySelector('.timeline-dividers');
        if (!dividersContainer) return;

        dividersContainer.innerHTML = '';

        const totalTicks = this.options.totalTicks || 100;
        const labelCount = this.options.labelCount || 10;
        const interval = totalTicks / labelCount;

        // 在每个分段边界处创建分隔线（除了第一个分段之前）
        for (let i = 1; i < labelCount; i++) {
            const dividerPosition = i * interval;
            const dividerPercentage = (dividerPosition / totalTicks) * 100;

            const divider = this.ui.createElement({
                className: 'timeline-divider',
                styles: {
                    left: `${dividerPercentage}%`,
                    position: 'absolute',
                    top: '0',
                    bottom: '0',
                    width: '1px',
                    backgroundColor: 'red',
                    pointerEvents: 'none'
                }
            });

            dividersContainer.appendChild(divider);
        }
    }

    /**
     * 添加关键点
     */
    addKeyframe(index: number, data?: any): Keyframe {
        // 确保位置在有效范围内（从0开始）
        const totalTicks = this.options.totalTicks || 100;
        const validPosition = Math.max(0, Math.min(index, totalTicks));

        // 检查是否已存在相同 position 的关键帧，如果存在则删除
        const existingKeyframe = this.keyframes.find(k => k.position === validPosition);
        if (existingKeyframe) {
            this.removeKeyframe(existingKeyframe.id);
        }

        const keyframe: Keyframe = {
            id: `keyframe-${++this.keyframeCounter}`,
            position: validPosition,
            index: this.keyframes.length,
            data
        };

        this.keyframes.push(keyframe);
        this.renderKeyframe(keyframe);
        this.sortKeyframes();

        return keyframe;
    }

    setKeyframes(keyframes: Keyframe[]): void {
        // this.clearKeyframes();
        keyframes.forEach(k => this.addKeyframe(k.index, { created: new Date() }));
    }

    clearKeyframes(): void {
        console.log('[timelineControlUI] 清空关键帧', this.keyframes);
        this.keyframes.forEach(k => {
            // 从DOM中移除
            const keyframeElement = this.trackElement.querySelector(`[data-keyframe-id="${k.id}"]`);
            if (keyframeElement) {
                keyframeElement.remove();
            }
        });
        this.keyframes = [];
        // 清空animationController的globalKeyframes
        const sceneViews = globalManager.getAllSceneInfos();
        sceneViews.forEach(sceneView => {
            sceneView.animationController.replaceGlobalKeyframes([]);
        });
    }
    /**
     * 删除关键点
     */
    removeKeyframe(keyframeId: string): boolean {
        const index = this.keyframes.findIndex(k => k.id === keyframeId);
        if (index === -1) return false;

        // 从DOM中移除
        const keyframeElement = this.trackElement.querySelector(`[data-keyframe-id="${keyframeId}"]`);
        if (keyframeElement) {
            keyframeElement.remove();
        }

        // 从数组中移除
        this.keyframes.splice(index, 1);

        // 更新索引
        this.keyframes.forEach((k, i) => k.index = i);
        console.log('删除关键帧', keyframeId, this.keyframes);
        return true;
    }

    /**
     * 渲染关键点
     */
    private renderKeyframe(keyframe: Keyframe): void {
        const keyframesContainer = this.trackElement.querySelector('.timeline-keyframes');
        if (!keyframesContainer) return;

        const keyframeElement = this.ui.createElement({
            className: 'timeline-keyframe',
            attributes: {
                'data-keyframe-id': keyframe.id,
                'data-position': keyframe.position.toString(),
                'data-index': keyframe.index.toString(),
                'title': keyframe.position.toString()
            }
        });

        // 创建图标
        const icon = this.ui.createIcon({
            iconName: 'guanjianzhen',
            className: 'keyframe-icon'
        });

        keyframeElement.appendChild(icon);

        // 点击事件
        keyframeElement.addEventListener('click', (e) => {
            e.stopPropagation();
            this.handleKeyframeClick(keyframe);
        });

        // 计算关键点位置，与对应刻度段分隔线垂直居中对齐
        // 关键点的 position 对应 timeline-divider 中的 data-tick
        // 分隔线位置计算：第i个分隔线的位置 = (i - 1) / totalTicks * 100%
        const totalTicks = this.options.totalTicks || 100;

        // 计算关键点对应的分隔线位置
        // keyframe.position 从1开始，对应 data-tick = keyframe.position 的分隔线
        const keyframePosition = keyframe.position; // 转换为基于0的值
        const keyframePercentage = (keyframePosition / totalTicks) * 100;

        // 设置关键点位置，使用 transform: translateX(-50%) 实现与分隔线的垂直居中对齐
        (keyframeElement as HTMLElement).style.left = `${keyframePercentage}%`;
        (keyframeElement as HTMLElement).style.transform = 'translateX(-50%)';

        keyframesContainer.appendChild(keyframeElement);
    }

    /**
     * 处理关键点点击
     */
    private handleKeyframeClick(keyframe: Keyframe): void {
        console.log('点击关键帧Keyframe clicked:', keyframe);
        this.setCurrentTick(keyframe.position);
    }

    /**
     * 对关键点按位置排序
     */
    private sortKeyframes(): void {
        this.keyframes.sort((a, b) => a.position - b.position);

        // 重新渲染所有关键点
        const keyframesContainer = this.trackElement.querySelector('.timeline-keyframes');
        if (keyframesContainer) {
            keyframesContainer.innerHTML = '';
            this.keyframes.forEach(k => this.renderKeyframe(k));
        }
    }

    /**
     * 设置当前刻度
     */
    updateCurrentTick(tick: number): void {
        const totalTicks = this.options.totalTicks || 100;
        this.currentTick = Math.max(0, Math.min(tick, totalTicks - 1)); // 从1开始到totalTicks
        console.log("设置当前刻度----", tick, this.currentTick)

        // 更新当前刻度标记
        let currentTickMarker = this.trackElement.querySelector('.timeline-current-tick') as HTMLElement;
        if (currentTickMarker) {
            currentTickMarker.style.display = 'block';

            // 直接根据刻度值计算位置，不受分段影响（tick从1开始，需要减1转换为基于0的值）
            const tickPosition = this.currentTick;
            const tickPercentage = (tickPosition / totalTicks) * 100;

            currentTickMarker.setAttribute('data-tick', this.currentTick.toString());
            currentTickMarker.style.left = `${tickPercentage}%`;

            // 更新当前刻度段显示（用于高亮标签）
            // this.updateCurrentTickSegment();
            this.updateCurrentTickLabel();
        }
    }

    /**
     * 处理当前刻度标签的拖拽开始
     */
    private handleLabelDragStart(e: MouseEvent): void {
        if (this.options.disabled) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        this.isDragging = true;
        
        // 添加拖拽样式
        if (this.currentTickLabel) {
            this.currentTickLabel.style.cursor = 'grabbing';
            this.currentTickLabel.style.userSelect = 'none';
        }

        // 创建全局鼠标移动和释放事件处理器
        this.dragMouseMoveHandler = (moveEvent: MouseEvent) => {
            if (!this.isDragging) return;
            this.handleLabelDragMove(moveEvent);
        };

        this.dragMouseUpHandler = (upEvent: MouseEvent) => {
            this.handleLabelDragEnd(upEvent);
        };

        // 添加全局事件监听器
        document.addEventListener('mousemove', this.dragMouseMoveHandler);
        document.addEventListener('mouseup', this.dragMouseUpHandler);
        document.addEventListener('mouseleave', this.dragMouseUpHandler); // 鼠标离开窗口时也结束拖拽
    }

    /**
     * 处理当前刻度标签的拖拽移动
     */
    private handleLabelDragMove(e: MouseEvent): void {
        if (!this.isDragging || !this.trackElement) return;

        const rect = this.trackElement.getBoundingClientRect();
        const trackWidth = rect.width;

        // 获取鼠标位置相对于轨道左边缘的偏移量
        const mouseX = e.clientX - rect.left;

        // 确保鼠标位置在轨道范围内
        const clampedMouseX = Math.max(0, Math.min(mouseX, trackWidth));

        const totalTicks = this.options.totalTicks || 100;

        // 计算鼠标位置对应的刻度值（步长为1）
        const tickPosition = (clampedMouseX / trackWidth) * totalTicks;
        const tick = Math.round(tickPosition);

        // 确保在有效范围内（0 到 totalTicks - 1）
        const finalTick = Math.max(0, Math.min(tick, totalTicks - 1));

        // 更新当前刻度
        this.setCurrentTick(finalTick);
    }

    /**
     * 处理当前刻度标签的拖拽结束
     */
    private handleLabelDragEnd(e: MouseEvent): void {
        if (!this.isDragging) return;

        this.isDragging = false;

        // 恢复样式
        if (this.currentTickLabel) {
            this.currentTickLabel.style.cursor = 'grab';
            this.currentTickLabel.style.userSelect = '';
        }

        // 移除全局事件监听器
        if (this.dragMouseMoveHandler) {
            document.removeEventListener('mousemove', this.dragMouseMoveHandler);
            this.dragMouseMoveHandler = null;
        }

        if (this.dragMouseUpHandler) {
            document.removeEventListener('mouseup', this.dragMouseUpHandler);
            document.removeEventListener('mouseleave', this.dragMouseUpHandler);
            this.dragMouseUpHandler = null;
        }
    }

    /**
     * 更新当前刻度标签显示
     * 在 timeline-labels 中增加一个 timeline-label
     * 位置与对应 timeline-divider 的 data-tick=currentTick 的分隔线垂直居中对齐
     */
    private updateCurrentTickLabel(): void {
        if (!this.labelsContainer) return;

        // 如果当前刻度未设置，不显示标签
        if (this.currentTick < 0) return;

        const totalTicks = this.options.totalTicks || 100;

        // 计算当前刻度标签的位置，与对应分隔线位置对齐
        // 分隔线位置计算：第i个分隔线的位置 = (i - 1) / totalTicks * 100%
        // 当前刻度标签对应分隔线 data-tick = currentTick，所以位置 = (currentTick - 1) / totalTicks * 100%
        const tickPosition = this.currentTick;
        const tickPercentage = (tickPosition / totalTicks) * 100;

        // 创建当前刻度标签
        // 使用 transform: translateX(-50%) 实现与分隔线的垂直居中对齐
        this.currentTickLabel = this.labelsContainer.querySelector('.timeline-label.timeline-current-tick-label') as HTMLElement;
        if (!this.currentTickLabel) {
            const currentTickLabel = this.ui.createElement({
                tag: 'span',
                className: 'timeline-label timeline-current-tick-label current-segment',
                styles: {
                    position: 'absolute',
                    transform: 'translateX(-50%)', // 垂直居中对齐，与分隔线对齐
                    cursor: 'grab', // 鼠标悬停时显示抓取手势
                    userSelect: 'none' // 禁止文本选择
                }
            });

            this.labelsContainer.appendChild(currentTickLabel);
            this.currentTickLabel = currentTickLabel;
            
            // 添加拖拽开始事件监听器
            this.currentTickLabel.addEventListener('mousedown', (e) => {
                this.handleLabelDragStart(e as MouseEvent);
            });
        }

        this.currentTickLabel.style.left = `${tickPercentage}%`;
        this.currentTickLabel.setAttribute('data-tick', this.currentTick.toString());
        this.currentTickLabel.textContent = this.currentTick.toString();
    }

    /**
     * 更新当前刻度段显示
     */
    private updateCurrentTickSegment(): void {
        // 移除 labelsContainer 中所有 timeline-label.timeline-current-tick-label 的子 DOM
        // const currentTickLabels = this.labelsContainer.querySelectorAll('.timeline-label.timeline-current-tick-label');
        // currentTickLabels.forEach(label => {
        //     label.remove();
        // });

        // const labels = this.labelsContainer.querySelectorAll('.timeline-label');
        // // labels.forEach(label => {
        // //     label.classList.remove('current-segment');
        // // });

        // const currentLabel = Array.from(labels).find(label => {
        //     return label.getAttribute('data-tick') === this.currentTick.toString();
        // });

        // if (currentLabel) {
        //     // currentLabel.classList.add('current-segment');
        //     console.log('当前lable-------currentLabel', currentLabel);
        // } else {
        //     this.updateCurrentTickLabel();
        // }
        
    }

    /**
     * 设置时间轴总刻度数
     */
    setTotalTicks(totalTicks: number): void {
        this.options.totalTicks = Math.max(1, totalTicks);
        this.container.setAttribute('data-total-ticks', this.options.totalTicks.toString());

        // 移除超出范围的关键点（position从1开始）
        this.keyframes = this.keyframes.filter(k => k.position <= (this.options.totalTicks || 100));

        // 重新渲染
        const keyframesContainer = this.trackElement.querySelector('.timeline-keyframes');
        if (keyframesContainer) {
            keyframesContainer.innerHTML = '';
            this.keyframes.forEach(k => this.renderKeyframe(k));
        }

        this.updateLabels();
        this.updateCurrentTick(Math.min(this.currentTick || 0, this.options.totalTicks || 100));
    }

    setCurrentTick(tick: number): void {
        this.updateCurrentTick(tick);
        this.onTickClickCallbacks.forEach(callback => {
            try {
                callback(tick);
            } catch (error) {
                console.error('Tick click callback error:', error);
            }
        });
    }

    setAnimationPlay(isPlaying: boolean): void {
        globalConfig.sceneParams.isPlaying = isPlaying;
        if (isPlaying) {
            this.isPlaying = true;
            this.lastFrameTime = 0; // 重置时间戳
            this.animate();
            console.log('开始播放动画', 'info');
        } else {
            this.isPlaying = false;

            if (this.animationId) {
                cancelAnimationFrame(this.animationId);
                this.animationId = null;
            }

            this.lastFrameTime = 0; // 重置时间戳
            sceneUtils.stopAnimation();

            console.log('停止播放动画', 'info');
        }
    }
    setFrameRate(frameRate: number): void {
        this.targetFrameTime = 1000 / frameRate;
    }
    animate() {
        if (!this.isPlaying) return;

        const currentTimestamp = performance.now();

        // 如果是第一帧，初始化时间戳
        if (this.lastFrameTime === 0) {
            this.lastFrameTime = currentTimestamp;
        }

        // 计算实际经过的时间（毫秒）
        const deltaTime = currentTimestamp - this.lastFrameTime;

        // 只有当经过的时间达到目标帧间隔时才更新动画
        if (deltaTime >= this.targetFrameTime) {
            // 基于帧率计算帧数增量
            // 每帧的时间 = 1000ms / frameRate
            // 经过的帧数 = deltaTime / (1000 / frameRate) = deltaTime * frameRate / 1000
            const frameIncrement = Math.floor((deltaTime * globalConfig.frameRate) / 1000);
            let currentTick = this.currentTick + frameIncrement;

            // 如果超过总帧数，循环回到开始
            if (currentTick >= globalConfig.totalFrames) {
                currentTick = currentTick % globalConfig.totalFrames;
            }

            this.setCurrentTick(currentTick);
            if (frameIncrement > 0) {
                // 更新上一帧时间戳
                this.lastFrameTime = currentTimestamp;
            }
        }
        // 继续下一帧
        this.animationId = requestAnimationFrame(() => this.animate());
    }
    /**
     * 添加关键点点击监听
     */
    onKeyframeClick(callback: (keyframe: Keyframe) => void): void {
        this.onChangeCallbacks.push(callback);
    }

    /**
     * 移除关键点点击监听
     */
    offKeyframeClick(callback: (keyframe: Keyframe) => void): void {
        const index = this.onChangeCallbacks.indexOf(callback);
        if (index > -1) {
            this.onChangeCallbacks.splice(index, 1);
        }
    }

    /**
     * 添加刻度点击监听
     */
    onTickClick(callback: (tick: number) => void): void {
        this.onTickClickCallbacks.push(callback);
    }

    /**
     * 移除刻度点击监听
     */
    offTickClick(callback: (tick: number) => void): void {
        const index = this.onTickClickCallbacks.indexOf(callback);
        if (index > -1) {
            this.onTickClickCallbacks.splice(index, 1);
        }
    }

    /**
     * 获取当前刻度
     */
    getCurrentTick(): number {
        const totalTicks = this.options.totalTicks || 100;
        if (this.currentTick >= 1 && this.currentTick <= totalTicks) {
            return this.currentTick;
        }
        return 0; // 0表示未设置
    }

    /**
     * 获取所有关键点
     */
    getKeyframes(): ReadonlyArray<Keyframe> {
        return [...this.keyframes];
    }

    /**
     * 获取容器元素
     */
    getContainer(): HTMLElement {
        return this.container;
    }

    /**
     * 销毁组件
     */
    destroy(): void {
        this.onChangeCallbacks = [];
        this.onTickClickCallbacks = [];

        // 清理拖拽事件监听器
        if (this.isDragging) {
            this.handleLabelDragEnd(new MouseEvent('mouseup'));
        }

        // 移除事件监听器
        this.ui.off('click', this.trackElement, () => { });

        // 移除DOM元素
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}

// 导出默认实例创建函数
export function createTimelineControlUI(options: TimelineControlOptions = {}): TimelineControlUI {
    return new TimelineControlUI(options);
}
